/**
 * `?firma=` (SOLO revisión, el contrato de ficha.html): pinta la ficha REAL
 * con una firma de demo encima, para comparar cada estado de la card con el
 * prototipo sin tener que firmar de verdad. Nada de esto viaja a la API: con
 * una vista forzada, los actos de firma avisan y no envían.
 */
import { NO_LO_SE, type Ficha, type Firma } from "@skein/contracts";

export const FIRMAS_FORZADAS = ["sin-firmar", "firmada", "volcada", "caducada", "asino", "nolose", "invalida", "no-dueno"] as const;
export type FirmaForzada = (typeof FIRMAS_FORZADAS)[number];
export const esFirmaForzada = (s: string | undefined): s is FirmaForzada => (FIRMAS_FORZADAS as readonly string[]).includes(s ?? "");

const DEMO = {
  firmada: "Sí, alrededor del 1 % para la industrial que vende a empresas. Con dos ferias al año rara vez baja del 1,2 %, y está bien así.",
  carla: "Sí, el 1 % para la industrial; en servicios a empresas lo pondría en el 1,5 %.",
  larga: "Sí, pero con matices: en la industrial que vende a empresas el 1 % vale si el equipo comercial es propio y las ferias son una o dos al año; si son tres o más, o si hay que entrar en un mercado nuevo, yo subiría hasta el 1,5 % o el 2 % durante los dos primeros años, y lo bajaría después en cuanto el coste de captar cada cliente se estabilice y el canal propio empiece a traer pedidos repetidos sin tener que volver a pagar por cada uno de ellos.",
  asino: "En una industrial con ferias el 1 % se queda corto: nosotros nunca bajamos del 1,5 %, y con menos se nota en los pedidos del año siguiente.",
};
const OTRO_HASH = "3c91a0f2".padEnd(64, "0");
const FIRMADO = "9d02c4a1".padEnd(64, "0");

/** La respuesta larga del estado `invalida` (la que en el prototipo pasa del tope de palabras). */
export const RESPUESTA_LARGA = DEMO.larga;

export function simularFirma(f: Ficha, me: { oid: string; name: string }, firma: FirmaForzada): Ficha {
  const ahora = new Date().toISOString();
  const n = Math.max(1, f.preguntas.length);
  const base = (p: Partial<Firma>): Firma => ({
    id: `demo-${firma}`, kind: "criterio", signer: me, baseHash: f.hash, resultHash: FIRMADO, decision: "vale",
    answers: Array.from({ length: n }, () => DEMO.firmada), comment: null, reference: null, createdAt: ahora,
    estado: "vigente", volcado: "pendiente", prUrl: null, resolucion: null, ...p,
  });
  const yo = { criterio: true, revision: false, despacho: false, porque: { revision: "Se puede revisar cuando el criterio esté volcado a git (de la casa)." } };
  const sobre = (p: Partial<Ficha>): Ficha => ({ ...f, status: "reviewed-ai", dueno: me, bloqueos: [], firmas: [], puedoFirmar: yo, ...p });
  switch (firma) {
    case "sin-firmar": case "invalida": return sobre({});
    case "firmada": return sobre({ firmas: [base({})] });
    case "volcada": return sobre({ status: "house", houseCriteriaBy: me.name, firmas: [base({ volcado: "volcada" })], puedoFirmar: { ...yo, criterio: false, porque: { ...yo.porque, criterio: "La ficha está en house.", revision: "Firmaste el criterio: la revisa otro socio." } } });
    case "caducada": return sobre({ firmas: [base({ baseHash: OTRO_HASH, resultHash: OTRO_HASH, estado: "caducada" })] });
    case "asino": return sobre({ bloqueos: ["criterio"], firmas: [base({ decision: "asi-no", answers: [], comment: DEMO.asino, resultHash: f.hash, volcado: "no-aplica" })] });
    case "nolose": return sobre({ firmas: [base({ answers: Array.from({ length: n }, () => NO_LO_SE), resultHash: f.hash })] });
    case "no-dueno": {
      const carla = { oid: "demo:carla", name: "Carla Pons" };
      return sobre({
        status: "house", houseCriteriaBy: carla.name, dueno: carla,
        firmas: [base({ signer: carla, answers: Array.from({ length: n }, () => DEMO.carla), volcado: "volcada" })],
        puedoFirmar: { criterio: false, revision: true, despacho: false, porque: { criterio: `El criterio de ${f.notebook} lo firma su dueña, Carla Pons.` } },
      });
    }
  }
}
